/**
 * EmptyState — what a list says when it has nothing in it.
 *
 * Three distinct cases, and conflating them is how an operator spends ten minutes
 * debugging a filter:
 *
 *   tone="default"   nothing exists yet          -> offer the action that creates one
 *   tone="filtered"  nothing matched the filter  -> offer to clear the filter
 *   tone="danger"    the request failed          -> say so, offer retry
 *
 * Always give `action` something. An empty state with no way forward is a dead end — and
 * for tone="filtered" the type MAKES it mandatory: "nothing matched" with no way to clear
 * the filter is the ten-minute confusion above, rendered as a dead end.
 */

import type { ReactNode } from 'react';

export type EmptyTone = 'default' | 'filtered' | 'danger';

interface EmptyStateBase {
  title: string;
  description?: ReactNode;
  /** Monospace mark. Defaults per tone. */
  glyph?: string;
  /** Primary action — "New schedule", "Clear filters", "Retry". */
  action?: ReactNode;
  /** Optional secondary action beside it. */
  secondaryAction?: ReactNode;
  className?: string;
}

/** A filtered-empty list must offer the way out of the filter. */
export type EmptyStateProps =
  | (EmptyStateBase & { tone?: Exclude<EmptyTone, 'filtered'> })
  | (EmptyStateBase & { tone: 'filtered'; action: ReactNode });

const DEFAULT_GLYPH: Record<EmptyTone, string> = {
  default: '□',
  filtered: '⌕',
  danger: '✕',
};

export function EmptyState({
  title,
  description,
  glyph,
  tone = 'default',
  action,
  secondaryAction,
  className = '',
}: EmptyStateProps) {
  return (
    <div className={`empty tone-${tone} ${className}`.trim()} role={tone === 'danger' ? 'alert' : undefined}>
      <div className="empty-glyph" aria-hidden="true">
        {glyph ?? DEFAULT_GLYPH[tone]}
      </div>
      <div className="empty-title">{title}</div>
      {description ? <div className="empty-description">{description}</div> : null}
      {action || secondaryAction ? (
        <div className="empty-actions">
          {action}
          {secondaryAction}
        </div>
      ) : null}
    </div>
  );
}

export default EmptyState;
